// Private DSL module. Do not re-export from index.ts.
import { FtIn } from './ft-in.js';
export function tokenize(input) {
    const tokens = [];
    let i = 0;
    while (i < input.length) {
        const ch = input[i];
        if (ch === ' ' || ch === '\t' || ch === '\n') {
            i++;
            continue;
        }
        if (ch === '(') {
            tokens.push({ type: 'lparen' });
            i++;
            continue;
        }
        if (ch === ')') {
            tokens.push({ type: 'rparen' });
            i++;
            continue;
        }
        if (ch === ',') {
            tokens.push({ type: 'comma' });
            i++;
            continue;
        }
        if (ch === '+' || ch === '-' || ch === '/' || ch === '*') {
            tokens.push({ type: 'op', value: ch });
            i++;
            continue;
        }
        // Feet-inches literal: [feet-inches] -> normalize to FtIn parse format
        if (ch === '[') {
            const end = input.indexOf(']', i);
            if (end === -1)
                throw new Error(`DSL: unterminated feet-inches literal at position ${i}`);
            const raw = input.slice(i + 1, end).trim();
            const dash = raw.indexOf('-');
            if (dash === -1)
                throw new Error(`DSL: invalid feet-inches literal "${raw}"`);
            const feet = raw.slice(0, dash);
            const rest = raw.slice(dash + 1);
            // rest could be "6", "06", "091", etc. Normalize inches portion to 2 digits.
            let inches;
            let eighths = '';
            if (rest.length <= 2) {
                inches = rest.padStart(2, '0');
            }
            else {
                // Prefer 2-digit inches if legal (<=11), else 1-digit.
                const twoDigit = parseInt(rest.slice(0, 2), 10);
                if (!isNaN(twoDigit) && twoDigit <= 11) {
                    inches = rest.slice(0, 2);
                    eighths = rest.slice(2);
                }
                else {
                    inches = rest.slice(0, 1).padStart(2, '0');
                    eighths = rest.slice(1);
                }
            }
            tokens.push({ type: 'ftin', value: `${feet}-${inches}${eighths}` });
            i = end + 1;
            continue;
        }
        // Number: digits with optional decimal
        if (/[0-9]/.test(ch)) {
            let j = i;
            while (j < input.length && /[0-9.]/.test(input[j]))
                j++;
            tokens.push({ type: 'number', value: parseFloat(input.slice(i, j)) });
            i = j;
            continue;
        }
        // Identifier: letters + digits (must start with letter).
        // A lone 'X' is kept as a single-character token so rewriteXAsMultiply
        // can promote it to '*' when it sits between two value-producing tokens.
        if (/[A-Za-z]/.test(ch)) {
            let j = i;
            if (ch === 'X') {
                // Always emit 'X' as a single-character ident token.
                j = i + 1;
            }
            else {
                while (j < input.length && /[A-Za-z0-9_]/.test(input[j])) {
                    if (input[j] === 'X')
                        break; // stop before embedded X
                    j++;
                }
            }
            tokens.push({ type: 'ident', value: input.slice(i, j) });
            i = j;
            continue;
        }
        throw new Error(`DSL: unexpected character "${ch}" at position ${i}`);
    }
    return rewriteXAsMultiply(tokens);
}
function rewriteXAsMultiply(tokens) {
    return tokens.map((t, idx) => {
        if (t.type === 'ident' && t.value === 'X') {
            const prev = tokens[idx - 1];
            const next = tokens[idx + 1];
            const prevIsValue = prev && (prev.type === 'number' || prev.type === 'ident' || prev.type === 'ftin' || prev.type === 'rparen');
            const nextIsValue = next && (next.type === 'number' || next.type === 'ident' || next.type === 'ftin' || next.type === 'lparen');
            if (prevIsValue && nextIsValue) {
                return { type: 'op', value: '*' };
            }
        }
        return t;
    });
}
class Parser {
    tokens;
    pos = 0;
    constructor(tokens) {
        this.tokens = tokens;
    }
    parse() {
        const node = this.expr();
        if (this.pos < this.tokens.length) {
            throw new Error(`DSL: trailing tokens at position ${this.pos}`);
        }
        return node;
    }
    // expr := term (('+'|'-') term)*
    expr() {
        let left = this.term();
        while (this.isAddOp(this.peek())) {
            const op = this.next().value;
            const right = this.term();
            left = { type: 'binop', op, left, right };
        }
        return left;
    }
    isAddOp(tok) {
        return !!tok && tok.type === 'op' && (tok.value === '+' || tok.value === '-');
    }
    // term := factor (('*'|'/') factor)*
    term() {
        let left = this.factor();
        while (this.peek()?.type === 'op' && (this.peek().value === '*' || this.peek().value === '/')) {
            const op = this.next().value;
            const right = this.factor();
            left = { type: 'binop', op, left, right };
        }
        return left;
    }
    // factor := number | ftin | ident ['(' args ')'] | '(' expr ')'
    factor() {
        const tok = this.peek();
        if (!tok)
            throw new Error('DSL: unexpected end of input');
        if (tok.type === 'number') {
            this.next();
            return { type: 'num', value: tok.value };
        }
        if (tok.type === 'ftin') {
            this.next();
            return { type: 'ftin', value: tok.value };
        }
        if (tok.type === 'lparen') {
            this.next();
            const inner = this.expr();
            const close = this.next();
            if (!close || close.type !== 'rparen')
                throw new Error('DSL: expected )');
            return inner;
        }
        if (tok.type === 'ident') {
            this.next();
            // Function call form: ident '(' args ')'
            if (this.peek()?.type === 'lparen') {
                this.next();
                const args = [];
                if (this.peek()?.type !== 'rparen') {
                    args.push(this.expr());
                    while (this.peek()?.type === 'comma') {
                        this.next();
                        args.push(this.expr());
                    }
                }
                const close = this.next();
                if (!close || close.type !== 'rparen')
                    throw new Error('DSL: expected ) after call');
                return { type: 'call', name: tok.value, args };
            }
            return { type: 'ident', value: tok.value };
        }
        throw new Error(`DSL: unexpected token ${JSON.stringify(tok)}`);
    }
    peek() { return this.tokens[this.pos]; }
    next() { return this.tokens[this.pos++]; }
}
export function parse(input) {
    return new Parser(tokenize(input)).parse();
}
export function evaluate(node, ctx) {
    switch (node.type) {
        case 'num':
            return FtIn.fromDecimalFeet(node.value);
        case 'ftin':
            return FtIn.parse(node.value);
        // Type discipline for arithmetic operators:
        //   The DSL has only one value type (FtIn) — there is no scalar type.
        //   For + and -, both operands are FtIn lengths and the result is an FtIn length.
        //   For * and /, the right operand is reinterpreted as a dimensionless multiplier
        //   by reading its decimal-feet value. This means `[5-0] * 2` evaluates as
        //   5 ft × 2.0 = 10 ft (because FtIn.fromDecimalFeet(2).toDecimalFeet() === 2),
        //   and `[3-0] * [0-06]` evaluates as 3 ft × 0.5 = 1.5 ft, NOT square feet.
        //   Callers that want true scalar semantics should still pass numeric literals
        //   (which round-trip cleanly through fromDecimalFeet/toDecimalFeet); the FtIn
        //   reinterpretation exists only because the DSL grammar has no scalar literal.
        //   Numerical consequence: because FtIn rounds to eighths of an inch, a
        //   scalar multiplier that doesn't land on the 1/8" grid (e.g., 1.33) will
        //   be quantized — `1.33 * X` evaluates as `1.3333… * X`, not `1.33 * X`.
        //   Formula authors should use clean-grid scalars or accept the rounding.
        case 'binop': {
            const l = evaluate(node.left, ctx);
            const r = evaluate(node.right, ctx);
            if (node.op === '+')
                return l.add(r);
            if (node.op === '-')
                return l.subtract(r);
            if (node.op === '*') {
                return l.multiply(r.toDecimalFeet());
            }
            if (node.op === '/') {
                const divisor = r.toDecimalFeet();
                if (divisor === 0)
                    throw new Error('DSL: division by zero');
                return l.multiply(1 / divisor);
            }
            throw new Error(`DSL: unknown operator ${node.op}`);
        }
        case 'ident':
            return evalIdent(node.value, ctx);
        case 'call':
            return evalCall(node.name, node.args, ctx);
    }
}
// Recognized dimension labels in bend formulas. The plan derives this list from
// the Shear-97 / ASA macro convention. Extend as Task 8's hand-encoded bend
// types reveal additional labels.
const dimLabels = ['A', 'B', 'C', 'E', 'F', 'G', 'H', 'J', 'O', 'R'];
// Fallback approximation for development length when no resolver is provided.
// ACI 12.2 prescribes a more nuanced formula; this conservative shortcut
// (48 × db, in inches) is a Phase 2 safety net.
const DEV_LENGTH_DB_MULTIPLIER_FALLBACK = 48;
function evalIdent(name, ctx) {
    // Dimension labels (A–R excluding D; D handled with context awareness below).
    if (dimLabels.includes(name)) {
        const val = ctx.dimensions?.[name];
        if (!val)
            throw new Error(`DSL: dimension ${name} not provided`);
        return val;
    }
    // "D" disambiguation: dimension if bendType declares it, otherwise development length.
    if (name === 'D') {
        if (ctx.dimensions && 'D' in ctx.dimensions)
            return ctx.dimensions.D;
        if (ctx.lookupDevLength)
            return ctx.lookupDevLength();
        // Phase 2 fallback: approximate development length = 48 × db
        return FtIn.fromDecimalInches(DEV_LENGTH_DB_MULTIPLIER_FALLBACK * ctx.barSize.nominalDiameter);
    }
    // Bar diameter (nominalDiameter is inches; return as FtIn).
    if (name === 'db') {
        return FtIn.fromDecimalInches(ctx.barSize.nominalDiameter);
    }
    // Standard hook codes: S90, S135, S180.
    if (/^S(90|135|180)$/.test(name)) {
        if (!ctx.lookupHookAdder) {
            throw new Error(`DSL: cannot resolve hook code "${name}" — lookupHookAdder not provided in context`);
        }
        return ctx.lookupHookAdder(name, ctx.barSize);
    }
    throw new Error(`DSL: unknown identifier "${name}"`);
}
function evalCall(name, args, ctx) {
    if (name === 'deduction') {
        if (args.length !== 1)
            throw new Error('DSL: deduction() takes exactly 1 argument');
        if (!ctx.lookupDeduction) {
            throw new Error('DSL: cannot resolve deduction() — lookupDeduction not provided in context');
        }
        const idxNode = args[0];
        if (idxNode.type !== 'num')
            throw new Error('DSL: deduction() argument must be a numeric literal');
        if (!Number.isInteger(idxNode.value) || idxNode.value < 0) {
            throw new Error('DSL: deduction() argument must be a non-negative integer');
        }
        return ctx.lookupDeduction(idxNode.value);
    }
    throw new Error(`DSL: unknown function "${name}"`);
}
